import { HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthState } from '@core/modules/auth/state/auth.state';
import { environment } from '@env/environment';
import { Store } from '@ngxs/store';
import { AppPersistedState } from '@app-state/app.persisted.state';
import { TRACEPARENT_HEADER, newTraceparent } from '@core/interceptors/trace-context';
import { ChatStateModel } from './state/chat.model';

export class ApiErrorResponse<T = unknown> {
  status?: number;
  headers?: HttpHeaders;
  url?: string;
  statusText?: string;
  error?: T;
  body?: T;
  sysErrorMessage?: string;
  displayErrorMessage?: string;
  errorCode?: string;
  activityId?: string;
  constructor(init: ApiErrorResponse<T>) {
    Object.assign(this, init);
  }
}

/**
 * `chat.socketId` read by state name: importing `ChatState` here (an eager root interceptor) pulled the
 * whole chat UI - Quill, socket.io-client - into the initial bundle. `null` until the chat state is registered.
 */
const selectChatSocketId = (state: { chat?: Pick<ChatStateModel, 'socketId'> }): string | null =>
  state.chat?.socketId ?? null;

export const ChatApiInterceptor: HttpInterceptorFn = (request, next) => {
  const store = inject(Store);

  if (!environment.urls.chatApi || !request.url.includes(environment.urls.chatApi)) return next(request);

  const setHeaders: Record<string, string | string[]> = {
    Accept: ['application/json'],
    'Accept-Language': store.selectSnapshot(AppPersistedState.language),
    'X-API-Key': environment.urls.chatApiKey!,
    Prefer: `timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    [TRACEPARENT_HEADER]: request.headers.get(TRACEPARENT_HEADER) ?? newTraceparent()
  };

  const accessToken = store.selectSnapshot(AuthState.accessToken);
  if (accessToken) setHeaders['Authorization'] = `Bearer ${accessToken}`;
  const socketId = store.selectSnapshot(selectChatSocketId);
  if (socketId) setHeaders['X-Connection-Id'] = socketId;

  request = request.clone({
    setHeaders,
    withCredentials: true
  });
  return next(request);
};
